import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { notEmpty } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import type { plusWidgetHomeV2Query$key } from "scmp-app/queries/__generated__/plusWidgetHomeV2Query.graphql";

import LogoPlus from "./logo-light-plus.svg";
import { HomeV2PlusWidgetArticle } from "./plus-widget-article";
import { ArticleList, Body, Container, CoverImage, LogoContainer } from "./styles";

type Props = {
  className?: string;
  reference: plusWidgetHomeV2Query$key;
};

export const HomeV2PlusWidget: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment plusWidgetHomeV2Query on Query {
        dailyPulseArticles: articles(
          filter: { typeIds: ["09ecd76b-0271-467d-be05-c228b9a8ef47"] }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
          first: 1
        ) {
          edges {
            node {
              entityId
              ...plusWidgetArticleHomeV2
            }
          }
        }
        plusArticles: articles(
          filter: { paywallTypeIds: ["716f570e-3083-4138-a080-47d3830fafe3"] }
          exclude: { typeIds: ["09ecd76b-0271-467d-be05-c228b9a8ef47"] }
          orderBy: { field: PUBLISHED_DATE, direction: DESC }
          first: 2
        ) {
          edges {
            node {
              entityId
              ...plusWidgetArticleHomeV2
            }
          }
        }
      }
    `,
    reference,
  );

  const asyncRosettaState = useAtomValue(rosettaAtom);
  const hasScmpPlusAccessRight = asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false;

  const articles = [...data.dailyPulseArticles?.edges, ...data.plusArticles?.edges]
    .map(edge => edge?.node)
    .filter(notEmpty)
    .filter(node => Boolean(node.entityId));
  const concatenatedEntityIds = articles.map(({ entityId }) => entityId).join(",");

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: concatenatedEntityIds,
        page_type: "homepage",
        product: "plus",
        widget_name: "scmp_plus",
      },
      subcategory: "widget",
    }),
    [concatenatedEntityIds],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  if (!hasScmpPlusAccessRight || articles.length === 0) return null;

  const [leadArticle] = articles;

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <LogoContainer>
        <LogoPlus />
      </LogoContainer>
      <Body>
        <ArticleList>
          {articles.map((article, index) => (
            <HomeV2PlusWidgetArticle
              key={article.entityId}
              reference={article}
              variant={index === 0 ? "lead" : "text"}
            />
          ))}
        </ArticleList>
        {leadArticle && (
          <CoverImage>
            <HomeV2PlusWidgetArticle reference={leadArticle} variant="cover" />
          </CoverImage>
        )}
      </Body>
    </Container>
  );
};

HomeV2PlusWidget.displayName = "HomeV2PlusWidget";
